import { Add, Code } from 'iconsax-reactjs';
import { type FC, useState } from 'react';
import { useMe } from '@/account/profile/api';
import {
  DEV_OVERRIDABLE_CONSTANTS,
  DEV_OVERRIDES_STORAGE_KEY,
  IS_PRODUCTION,
} from '@/base/constants';
import { Button } from '@/shared/Button';
import { Checkbox } from '@/shared/Checkbox';
import { Dialog } from '@/shared/Dialog';
import { Input } from '@/shared/Input';
import { usePromise } from '@/shared/react';
import {
  DEV_FEATURE_OVERRIDES_STORAGE_KEY,
  devFeatureOverrides,
  hasActiveDevFeatureOverrides,
} from './featureOverrides';

const hasActiveOverrides =
  DEV_OVERRIDABLE_CONSTANTS.some((c) => c.overrideValue !== null || c.unset) ||
  hasActiveDevFeatureOverrides;

// Intentionally not translated — internal tool, gated behind the `dev` feature.
const DevToolsDialog: FC<{ open?: boolean; onClose?: () => void }> = ({
  open,
  onClose,
}) => {
  const me = useMe();
  const serverFeatures = me.data?.features ?? [];

  // Draft value `null` means "unset": resolve to undefined, no build fallback.
  const [draft, setDraft] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(
      DEV_OVERRIDABLE_CONSTANTS.map((c) => [
        c.key,
        c.unset ? null : (c.overrideValue ?? ''),
      ]),
    ),
  );
  const [added, setAdded] = useState<string[]>(devFeatureOverrides.added);
  const [removed, setRemoved] = useState<string[]>(devFeatureOverrides.removed);
  const [newFlag, setNewFlag] = useState('');

  const addFlag = () => {
    const flag = newFlag.trim();
    if (!flag) return;
    if (serverFeatures.includes(flag)) {
      setRemoved((prev) => prev.filter((x) => x !== flag));
    } else if (!added.includes(flag)) {
      setAdded((prev) => [...prev, flag]);
    }
    setNewFlag('');
  };

  const save = () => {
    const overrides: Record<string, string | null> = {};
    for (const constant of DEV_OVERRIDABLE_CONSTANTS) {
      const raw = draft[constant.key];
      if (raw === null) {
        overrides[constant.key] = null;
        continue;
      }
      const value = raw?.trim();
      if (value && value !== constant.buildValue)
        overrides[constant.key] = value;
    }
    if (Object.keys(overrides).length > 0) {
      localStorage.setItem(
        DEV_OVERRIDES_STORAGE_KEY,
        JSON.stringify(overrides),
      );
    } else {
      localStorage.removeItem(DEV_OVERRIDES_STORAGE_KEY);
    }
    if (added.length > 0 || removed.length > 0) {
      localStorage.setItem(
        DEV_FEATURE_OVERRIDES_STORAGE_KEY,
        JSON.stringify({ added, removed }),
      );
    } else {
      localStorage.removeItem(DEV_FEATURE_OVERRIDES_STORAGE_KEY);
    }
    window.location.reload();
  };

  const resetAll = () => {
    localStorage.removeItem(DEV_OVERRIDES_STORAGE_KEY);
    localStorage.removeItem(DEV_FEATURE_OVERRIDES_STORAGE_KEY);
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      position="bottom"
      className="!font-mono"
      header={
        <h2 className="text-lg font-semibold font-mono" dir="ltr">
          Dev Tools
        </h2>
      }
      footer={
        <div className="grid grid-cols-2 w-full items-center gap-2" dir="ltr">
          <Button size={10} variant="outline" onClick={resetAll}>
            Reset All
          </Button>
          <Button size={10} variant="brand" block onClick={save}>
            Save & Reload
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" dir="ltr">
        <h3 className="font-semibold">Constants</h3>
        <p className="text-sm text-content-tertiary">
          Override build constants for this browser only. Empty fields fall back
          to the build value; unset makes the constant undefined with no
          fallback. Saving reloads the page.
        </p>
        {DEV_OVERRIDABLE_CONSTANTS.map((constant) => (
          <label key={constant.key} className="flex flex-col gap-1">
            <span className="text-sm font-medium">
              {constant.label}
              {constant.overrideValue !== null && (
                <span className="ms-2 text-xs text-content-negative">
                  overridden
                </span>
              )}
              {constant.unset && (
                <span className="ms-2 text-xs text-content-negative">
                  unset
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <Input
                size={10}
                block
                dir="ltr"
                disabled={draft[constant.key] === null}
                placeholder={
                  draft[constant.key] === null
                    ? 'undefined'
                    : constant.buildValue || '—'
                }
                value={draft[constant.key] ?? ''}
                onChange={(value) => {
                  setDraft((prev) => ({
                    ...prev,
                    [constant.key]: value ?? '',
                  }));
                }}
              />
              <Button
                size={10}
                variant={draft[constant.key] === null ? 'negative' : 'outline'}
                className="shrink-0"
                onClick={() => {
                  setDraft((prev) => ({
                    ...prev,
                    [constant.key]:
                      prev[constant.key] === null
                        ? (constant.overrideValue ?? '')
                        : null,
                  }));
                }}
              >
                Unset
              </Button>
            </div>
            <span className="text-xs text-content-tertiary font-mono">
              {constant.key}
            </span>
          </label>
        ))}

        <h3 className="font-semibold mt-2">Feature Flags</h3>
        <p className="text-sm text-content-tertiary">
          Uncheck an account flag to disable it for yourself, or add a manual
          flag. Applies to this browser only.
        </p>
        {serverFeatures.length > 0 && (
          <Checkbox
            multiple
            indicator="before"
            className="flex flex-col gap-2"
            options={serverFeatures.map((feature) => ({
              key: feature,
              value: feature,
              label: <span className="font-mono text-sm">{feature}</span>,
            }))}
            value={serverFeatures.filter((f) => !removed.includes(f))}
            onChange={(active) => {
              setRemoved(serverFeatures.filter((f) => !active?.includes(f)));
            }}
          />
        )}
        {added.map((flag) => (
          <div key={flag} className="flex items-center justify-between gap-2">
            <span className="font-mono text-sm">
              {flag}
              <span className="ms-2 text-xs text-content-positive">manual</span>
            </span>
            <Button
              fab
              size={6}
              variant="ghost"
              aria-label={`Remove ${flag}`}
              onClick={() => {
                setAdded((prev) => prev.filter((x) => x !== flag));
              }}
            >
              <Add size={16} className="rotate-45" />
            </Button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <Input
            size={10}
            block
            dir="ltr"
            placeholder="feature-name"
            value={newFlag}
            onChange={(value) => setNewFlag(value ?? '')}
          />
          <Button
            fab
            size={10}
            variant="outline"
            aria-label="Add Feature"
            onClick={addFlag}
            className="shrink-0"
          >
            <Add size={16} />
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

const DevToolsTrigger: FC = () => {
  const dialog = usePromise();

  return (
    <>
      <Button
        fab
        size={8}
        variant="outline"
        aria-label="Dev tools"
        className="shrink-0"
        onClick={() => {
          dialog.run();
        }}
      >
        <Code size={18} />
        {hasActiveOverrides && (
          <span className="absolute top-0.5 end-0.5 size-2 rounded-full bg-content-negative" />
        )}
      </Button>
      <DevToolsDialog
        open={dialog.isRunning}
        onClose={() => dialog.resolve()}
      />
    </>
  );
};

export const DevToolsButton: FC = () => {
  const me = useMe();
  // Visibility checks raw server features (not hasFeatureGateHook), so
  // removing `dev` via the panel can't lock you out of the panel itself.
  const features = me.data?.features ?? [];
  const visible =
    !IS_PRODUCTION || features.includes('//all') || features.includes('dev');
  if (!visible) return null;
  return <DevToolsTrigger />;
};
